/* eslint class-methods-use-this: 0 */
export default class DocsunVitalsHelper {
  constructor(vitalDict = {}) {
    this.vitalDict   = vitalDict;
    this.vitalOutput = {};
    this.#buildOutput();
  }

  /* read-only snapshot --------------------------------------- */
  get output() {
    return this._out || (this._out = Object.freeze({ ...this.vitalOutput }));
  }
  get(key) { return this.vitalOutput[key]; }

  /* ----------------------------------------------------------- *
   * convertRating                                               *
   * Docsun scores: 1  (best)  … 5 (worst)                       *
   * UI needs:    100 (best) … 33 (worst)                        *
   * Mapping is no longer linear 25-step; we use a lookup table  *
   *     1 → 100 %   2 → 85 %   3 → 67 %   4 → 50 %   5 → 33 %   *
   * ----------------------------------------------------------- */
  static convertRating(rating) {
    if (rating < 1 || rating > 5) {
      throw new RangeError('Rating must be between 1 and 5');
    }
    /* round to nearest int so 3.2 behaves like 3 */
    const idx = Math.round(rating);
    const LUT = [0, 100, 85, 67, 50, 33];   // 0th slot unused
    return LUT[idx];
  }

  /* build composite output once -------------------------------- */
  #buildOutput() {
    Object.assign(this.vitalOutput, this.#heartRate());
    Object.assign(this.vitalOutput, this.#breathingRate());
    Object.assign(this.vitalOutput, this.#spo2());
    Object.assign(this.vitalOutput, this.#bloodPressure());
    Object.assign(this.vitalOutput, this.#bloodSugar());
    Object.assign(this.vitalOutput, this.#cholesterol());
  }

  /* individual vital scorers ----------------------------------- */
  #heartRate() {
    const hr = Math.round(this.vitalDict.hr ?? 0);
    let score = 5;
    if (hr <= 60)       score = 1;
    else if (hr <= 75)  score = 2;
    else if (hr <= 85)  score = 3;
    else if (hr <= 100) score = 4;
    return { hr, hr_score: score };
  }

  #spo2() {
    const spo2 = Math.round(this.vitalDict.spo2 ?? 0);
    let score = 1;
    if (spo2 <= 90)       score = 5;
    else if (spo2 <= 92)  score = 4;
    else if (spo2 <= 94)  score = 3;
    else if (spo2 <= 96)  score = 2;
    return { spo2, spo2_score: score };
  }

  #breathingRate() {
    const br = Math.round(this.vitalDict.br ?? 0);
    let score = 1;
    if      (br <  8) score = 1;   // very low → treat as best
    else if (br < 12) score = 1;
    else if (br < 16) score = 2;
    else if (br < 20) score = 3;
    else if (br < 24) score = 4;
    else              score = 5;
    return { br, br_score: score };
  }

  #bloodPressure() {
    const syst  = Math.round(this.vitalDict.syst  ?? 0);
    const diast = Math.round(this.vitalDict.diast ?? 0);
    let score = 5;
    if      (syst <=  90 && diast <= 60) score = 1;
    else if (syst <= 120 && diast <= 80) score = 2;
    else if (syst <= 129 && diast <= 80) score = 3;
    else if (syst <= 139 && diast <= 90) score = 4;
    return { bp: `${syst}/${diast}`, bp_score: score };
  }

  #bloodSugar() {
    const bs = Number(this.vitalDict.bs ?? 0);
    console.log('Blood sugar in context : ',bs)
    let score = 5;
    if      (bs <= 4)     score = 1;
    else if (bs <= 5.2)   score = 2;
    else if (bs <= 7.1)   score = 3;
    else if (bs <= 10)    score = 4;
    return { bs, bs_score: score };
  }

  #cholesterol() {
    const cholMmol = Number(this.vitalDict.chol ?? 0); 
    const cholMg = (Number(this.vitalDict.chol ?? 0) *38.67).toFixed(0);
    console.log('Cholesterol in context : ',cholMg)
    let score = 5;
    if      (cholMg <= 140) score = 1;
    else if (cholMg <= 180) score = 2;
    else if (cholMg <= 200) score = 3;
    else if (cholMg <= 240) score = 4;
    return { chol: cholMmol, chol_score: score };
  }
}
